import { useState, useEffect, ChangeEvent, MouseEvent } from 'react';
import OutsideAlerter from '../outsidealerter/OutsideAlerter';
import Button from '../button/Button';

import {
  DropdownContainer,
  DropdownWrapper,
  Element,
  ElementButton,
  Elements,
  SearchInput,
} from './DropDown.styles';

import Span from '../texts/span/Span';
import Label from '../texts/label/Label';
import theme from '../../styles/theme';

const defaultProps = {
  disabled: false,
  status: 'normal',
  searchable: false,
};

export type ItemProp = {
  id?: string;
  label: string;
  disabled?: boolean;
};

type DropDownProps = {
  id: string;
  disabled?: boolean;
  label?: string;
  marginTop?: string;
  items?: Array<ItemProp>;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  placeholderTitle?: string;
  children?: any;
  status?: 'normal' | 'error';
  variant?: 'xl';
  searchable?: boolean;
  searchPlaceholder?: string;
  onChange: (title: string, item?: ItemProp) => void;
  renderItem?: (item: ItemProp) => React.ReactNode; // Función para renderizar items personalizados
  footerContent?: React.ReactNode; // Contenido a renderizar al final de la lista
};

const DropDown = (props: DropDownProps) => {
  const { children, disabled, items, size, title, placeholderTitle, status, variant, marginTop, searchable, searchPlaceholder, renderItem, footerContent, id } = props;
  const showPlaceholderStyle = placeholderTitle != null && title === placeholderTitle;
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toggle = () => setOpen(!open);

  const effectiveVariant =
    variant ?? (id && id.startsWith('document-type-') ? 'instrumentation' : undefined);

  // Resetear el término de búsqueda cuando se cierra el dropdown
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);

  // Filtrar items basado en el término de búsqueda
  const filteredItems = items && searchable && searchTerm
    ? items.filter(item => 
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  const renderDropdownContent = () => {
    return filteredItems
      ? filteredItems.map((item) => (
        <Element key={item.id ?? item.label}>
          <ElementButton
            type='button'
            onClick={() => callItemAction(item)}
            disabled={item.disabled}
          >
            {renderItem ? (
              renderItem(item)
            ) : (
              <Span
                variant='neutral'
                modifier={item.disabled ? 'weak' : 'strong'}
                type='2'
              >
                {item.label}
              </Span>
            )}
          </ElementButton>
        </Element>
      ))
      : children;
  };

  const callItemAction = (item: ItemProp) => {
    toggle();
    props.onChange(item.label, item);
  };

  const wrapperMarginTop = marginTop;

  return (
    <DropdownContainer variant={effectiveVariant} status={status}>
      <OutsideAlerter handleOutSideClick={() => setOpen(false)} active={open}>
        <DropdownWrapper
          status={status}
          variant={effectiveVariant}
          disabled={disabled}
          style={wrapperMarginTop ? { marginTop: wrapperMarginTop } : undefined}
        >
          {props.label && (
            marginTop ? (
              <div style={{ marginTop: marginTop }}>
                <Label id={props.id} type={'4'} modifier='strong'>
                  {props.label}
                </Label>
              </div>
            ) : (
              <Label id={props.id} type={'4'} modifier='strong'>
                {props.label}
              </Label>
            )
          )}
          <Button
            variant={'contained'}
            size={size}
            type='button'
            fullWidth
            onClick={toggle}
            disabled={disabled}
            icon={open ? 'chevron_up_small' : 'chevron_down_small'}
            iconAlign='right'
            status={status}
          >
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
              ...(showPlaceholderStyle
                ? { color: theme.colors.neutral.text.weak }
                : {}),
            }}>
              {title}
            </span>
          </Button>
          {open && (
            <Elements variant={effectiveVariant}>
              {searchable && items && (
                <SearchInput
                  type="text"
                  placeholder={searchPlaceholder || 'Buscar...'}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  autoFocus
                  onClick={(e: MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                />
              )}
              {renderDropdownContent()}
              {footerContent && (
                <Element style={{ borderTop: '1px solid #e0e0e0', marginTop: '4px', padding: 0 }}>
                  {footerContent}
                </Element>
              )}
            </Elements>
          )}
        </DropdownWrapper>
      </OutsideAlerter>
    </DropdownContainer>
  );
};

DropDown.defaultProps = defaultProps;
export default DropDown;
